import { AgentCategory } from "~/shared/enums";
import type { AgentConfig } from "~/shared/types";
import { makeValidateRegex, validateIsNotEmpty } from "~/utils/validation";

import AgentIcon from "./AgentIcon.vue";
import Icon from "./Icon.vue";

const BRAND_ID_REGEX = /^[A-Za-z0-9_-]+$/;
const validateBrandId = makeValidateRegex(BRAND_ID_REGEX, "Enter a valid Predis brand ID", true);

const config: AgentConfig = {
  name: "Predis",
  category: AgentCategory.MARKETING,
  icon: Icon,
  agentIcon: AgentIcon,
  color: "#296Bd6",
  helpUrl: "https://help.dartai.com/en/articles/11651984-ai-social-media-agent",
  description: "Create and schedule branded social posts, images, and videos straight from Dart",
  descriptionMarkdown: `## Overview

Integrate Dart with Predis.ai to streamline social media content creation and publishing workflows. This integration enables automated task creation for content campaigns, scheduled publishing coordination, and performance tracking across all your social media channels.

The integration synchronizes your social media calendar with Dart tasks, automatically generating content creation tasks based on your publishing schedule and brand guidelines. AI-generated content can be reviewed and approved through Dart's task management system before publication.

## Features

- Automated content generation for posts, reels, carousels, and stories from simple prompts
- Multi-platform publishing to Instagram, Facebook, LinkedIn, Twitter, and TikTok
- Brand-consistent design with AI that learns your visual identity and voice
- Content approval workflows integrated with Dart task management
- Automated hashtag generation and trending audio recommendations
- Performance analytics and competitor analysis for content optimization

## Setup guide

1. Create or use existing Predis account
2. Head to the [brands page](https://app.predis.ai/app/settings/?tab=usage) to get your brand ID
3. Go to the [API page](https://app.predis.ai/app/settings/?tab=restapi) to get your API key
4. Navigate to Dart agents page
5. Paste your brand ID and API key in respective fields
6. Copy the \`Dart webhook URL\` from Dart and paste it back in the [Predis API page](https://app.predis.ai/app/settings/?tab=restapi)
7. Assign content tasks to your Predis agent in any accessible dartboard`,

  configFields: [
    {
      key: "orgId",
      type: "text",
      label: "Brand ID",
      placeholder: "Your Predis brand ID",
      validate: validateBrandId,
    },
    {
      key: "apiKey",
      type: "password",
      label: "API key",
      placeholder: "Your Predis API key",
      validate: validateIsNotEmpty,
    },
    {
      type: "webhook",
      key: "webhook",
    },
  ],
};

export default config;
